import React, { useEffect, useMemo, useRef, useState } from 'react';
import { palette, fonts } from './styles';

const PASSPHRASE = (process.env.REACT_APP_GSK_PASSPHRASE || 'gsklovesrealpatients').trim().toLowerCase();
const UNLOCK_KEY = 'rp_gsk_unlock';
const UNLOCK_TTL_MS = 90 * 24 * 60 * 60 * 1000;

const isUnlocked = () => {
  try {
    const raw = localStorage.getItem(UNLOCK_KEY);
    if (!raw) return false;
    const data = JSON.parse(raw);
    return Boolean(data?.ts) && Date.now() - data.ts < UNLOCK_TTL_MS;
  } catch {
    return false;
  }
};

const persistUnlock = () => {
  try {
    localStorage.setItem(UNLOCK_KEY, JSON.stringify({ ts: Date.now() }));
  } catch {
    /* storage unavailable — gate re-shows next visit */
  }
};

const stripQueryParam = () => {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('k')) return;
    url.searchParams.delete('k');
    const next = url.pathname + (url.searchParams.toString() ? `?${url.searchParams}` : '') + url.hash;
    window.history.replaceState({}, '', next);
  } catch {
    /* no-op */
  }
};

const checkAutoUnlock = () => {
  try {
    const k = (new URLSearchParams(window.location.search).get('k') || '').trim().toLowerCase();
    return k && k === PASSPHRASE;
  } catch {
    return false;
  }
};

export const GSKGate = ({ children }) => {
  const [unlocked, setUnlocked] = useState(false);
  const [checked, setChecked] = useState(false);
  const [entry, setEntry] = useState('');
  const [error, setError] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isUnlocked()) {
      setUnlocked(true);
    } else if (checkAutoUnlock()) {
      persistUnlock();
      stripQueryParam();
      setUnlocked(true);
    }
    setChecked(true);
  }, []);

  useEffect(() => {
    if (checked && !unlocked) {
      document.body.style.overflow = 'hidden';
      const id = setTimeout(() => inputRef.current?.focus(), 80);
      return () => {
        document.body.style.overflow = '';
        clearTimeout(id);
      };
    }
    document.body.style.overflow = '';
    return undefined;
  }, [checked, unlocked]);

  const submit = (e) => {
    e.preventDefault();
    const guess = entry.trim().toLowerCase();
    if (guess === PASSPHRASE) {
      persistUnlock();
      setUnlocked(true);
      setError(false);
    } else {
      setError(true);
    }
  };

  const wrapStyle = useMemo(
    () => ({
      position: 'fixed',
      inset: 0,
      zIndex: 10000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      background:
        'linear-gradient(135deg, rgba(250,190,192,0.55) 0%, rgba(245,215,170,0.45) 50%, rgba(181,201,168,0.4) 100%), #FAF8F6',
      boxSizing: 'border-box',
      overflowY: 'auto',
    }),
    []
  );

  if (!checked) return null;
  if (unlocked) return children;

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="gsk-gate-title" style={wrapStyle}>
      <div
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: '440px',
          background: '#fff',
          borderRadius: '16px',
          padding: '48px 40px 40px',
          boxShadow: '0 24px 60px rgba(0,0,0,0.18)',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            lineHeight: 1.1,
            fontFamily: fonts.serif,
            fontSize: '20px',
            fontWeight: 700,
            color: palette.ink,
            letterSpacing: '-0.2px',
            marginBottom: '24px',
          }}
        >
          <span>Real</span>
          <span style={{ position: 'relative', display: 'inline-block', marginLeft: '7px' }}>
            <span
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: '-2%',
                right: '-8%',
                bottom: 0,
                height: '50%',
                background:
                  'linear-gradient(90deg, rgba(235,160,155,0.95) 0%, rgba(245,185,165,0.9) 40%, rgba(250,200,160,0.85) 70%, rgba(245,215,170,0.7) 100%)',
                transform: 'skewX(-2deg) rotate(-1deg)',
                zIndex: 0,
                borderRadius: '3px 6px 5px 3px',
              }}
            />
            <span style={{ position: 'relative', zIndex: 1 }}>Patients</span>
          </span>
          <sup style={{ fontSize: '11px', fontFamily: fonts.sans, fontWeight: 400, marginLeft: '2px' }}>™</sup>
        </div>

        <p
          style={{
            fontFamily: fonts.sans,
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: palette.rust,
            margin: '0 0 12px',
          }}
        >
          Private · GSK Capabilities
        </p>
        <h2
          id="gsk-gate-title"
          style={{
            fontFamily: fonts.serif,
            fontSize: '24px',
            fontWeight: 700,
            color: palette.ink,
            lineHeight: 1.25,
            margin: '0 0 10px',
          }}
        >
          This page is for invited reviewers.
        </h2>
        <p
          style={{
            fontFamily: fonts.sans,
            fontSize: '15px',
            color: palette.muted,
            lineHeight: 1.6,
            margin: '0 0 24px',
          }}
        >
          Enter the passphrase shared with you to continue.
        </p>

        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <input
            ref={inputRef}
            type="password"
            placeholder="Passphrase"
            value={entry}
            onChange={(e) => {
              setEntry(e.target.value);
              if (error) setError(false);
            }}
            autoComplete="off"
            spellCheck={false}
            style={{
              fontFamily: fonts.sans,
              fontSize: '15px',
              padding: '13px 16px',
              borderRadius: '8px',
              border: `1px solid ${error ? palette.rust : palette.border}`,
              backgroundColor: '#FAF8F6',
              color: palette.ink,
              outline: 'none',
              boxSizing: 'border-box',
              width: '100%',
            }}
          />
          <button
            type="submit"
            style={{
              fontFamily: fonts.sans,
              fontSize: '15px',
              fontWeight: 600,
              padding: '14px 24px',
              borderRadius: '8px',
              border: 'none',
              background: 'linear-gradient(135deg, #EBA09B 0%, #F5B9A5 100%)',
              color: '#fff',
              boxShadow: '0 6px 20px rgba(235,160,155,0.35)',
              marginTop: '4px',
              cursor: 'pointer',
            }}
          >
            Continue
          </button>
        </form>

        {error && (
          <p
            style={{
              fontFamily: fonts.sans,
              fontSize: '13px',
              color: palette.rust,
              marginTop: '14px',
              textAlign: 'center',
            }}
          >
            That passphrase didn't match. Double-check the link you were sent, or{' '}
            <a href="mailto:sbd@realpatients.com" style={{ color: palette.rust }}>
              email us
            </a>
            .
          </p>
        )}

        <p
          style={{
            fontFamily: fonts.sans,
            fontSize: '12px',
            color: palette.muted,
            marginTop: '28px',
            textAlign: 'center',
            lineHeight: 1.5,
          }}
        >
          Real Patients™ · Private capabilities preview
        </p>
      </div>
    </div>
  );
};
